/**
 * Komprimerer bilder i browseren før opplasting til Supabase Storage.
 * Mål: redusere fra typiske 4 MB iPhone-originaler til < 500 KB ved å
 *       resize til maks 1920px bredde og rendre som JPEG kvalitet 85.
 *
 * Vi gjør dette i browseren (canvas) i stedet for å bruke Supabase Image
 * Transformations, så vi sparer både Storage-plass og overføringskvote.
 * Free-tier-prosjekter får dette gratis.
 *
 * Edge cases:
 * - SVG returneres uendret (vektor — komprimering er meningsløst)
 * - Bilder mindre enn maxWidth + under maxBytes returneres uendret
 * - Hvis komprimering feiler (f.eks. eldre browser), returneres original
 */

interface CompressOptions {
  /** Maksimal bredde i piksler. Default 1920. */
  maxWidth?: number;
  /** Maksimal høyde i piksler. Default 1920. */
  maxHeight?: number;
  /** JPEG-kvalitet (0–1). Default 0.85. */
  quality?: number;
  /** Hopp over komprimering hvis fil allerede er under denne størrelsen. Default 500 kB. */
  skipBelowBytes?: number;
}

const DEFAULTS: Required<CompressOptions> = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.85,
  skipBelowBytes: 500 * 1024,
};

export async function compressImage(file: File, options: CompressOptions = {}): Promise<File> {
  const opts = { ...DEFAULTS, ...options };

  // SVG og andre vektor-formater skal ikke rendres på canvas — pass through.
  if (file.type === "image/svg+xml") return file;
  if (!file.type.startsWith("image/")) return file;

  // Allerede liten nok? La være.
  if (file.size <= opts.skipBelowBytes) {
    // Sjekk likevel om vi bør resize (stor oppløsning men lite filsize er
    // sjeldent, men kan skje). Hopp over for enkelhet — størrelse er dominant.
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;

    // Skaler ned bare hvis nødvendig.
    const scale = Math.min(opts.maxWidth / width, opts.maxHeight / height, 1);
    const targetWidth = Math.round(width * scale);
    const targetHeight = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", opts.quality)
    );
    if (!blob) return file;

    // Hvis komprimert versjon er større enn original (sjelden, men f.eks. for
    // små bilder eller når de allerede er WebP), bruk original.
    if (blob.size >= file.size) return file;

    const baseName = file.name.replace(/\.[^.]+$/, "") || "image";
    return new File([blob], `${baseName}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    // Hvis canvas/createImageBitmap feiler — bruk originalen.
    return file;
  }
}
